import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import CollectionUtils from "@/utils/common/CollectionUtils.ts";
import {SerialRange} from "@/utils/presenters/interfaces.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";

export default class ItemSerialNumberUtil {
  private static serialSeparator: string = ',';
  private static serialRangeSeparator: string = ':';

  static unZipSerials(serials: string | undefined): string[] {
    if (!serials)
      return [];
    let serialRanges: string[] = serials.split(this.serialSeparator);
    let retList: string[] = [];
    for (let serialRange of serialRanges) {
      if (serialRange)
        retList.push(...this.unzipSerialRange(serialRange));
    }
    return retList;
  }

  static zipSerialRange(serials: string[]): string {
    return StringUtils.toCSVLineWithSep(this.serialSeparator, this.zipSerialRangeAsList(serials));
  }

  static checkDuplicatesSerials(text: string): string {
    let unZipSerials: string[] = StringUtils.csvLineToList(text, "\n");
    unZipSerials.sort();
    let duplicates: string[] = [];
    let hasDuplicates: boolean = false;
    for (let i = unZipSerials.length - 1; i >= 1; i--) {
      if (unZipSerials[i] === unZipSerials[i - 1]) {
        const removed = unZipSerials.splice(i, 1)[0];
        duplicates.push(removed);
        hasDuplicates = true;
      }
    }
    if (hasDuplicates) {
      useErrorsStore().pushErrorStr("The system found duplicate serials, " + StringUtils.toCSVLineWithSep(',', duplicates) + " - the duplicates were removed");
    }
    return text;
  }

  private static zipSerialRangeAsList(serials: string[]): string[] {
    CollectionUtils.sortAndRemoveDuplicates(serials, (a, b) => {
      return this.decimalValue(a) - this.decimalValue(b);
    });
    let ranges: SerialRange[] = [];
    for (let current of serials) {
      let last: SerialRange = ranges[ranges.length - 1];
      if (!last) {
        ranges.push({first: current, second: current});
        continue;
      }
      let previousRangeEnd: string = last.second ?? '';
      if (this.notConsistentWith(previousRangeEnd, current)) {
        ranges.push({first: current, second: current});
        continue;
      }
      let previousDecimal: number = this.decimalValue(previousRangeEnd);
      let currentDecimal: number = this.decimalValue(current);
      if (Math.floor(currentDecimal - previousDecimal) == 1)
        last.second = current;
      else
        ranges.push({first: current, second: current});
    }
    return ranges.map(r => r.first === r.second ? r.first : r.first + this.serialRangeSeparator + r.second);
  }

  private static decimalValue(serial: string): number {
    let substring: string = serial.substring(serial.length - this.calcDecimalPartSize(serial));
    if (!substring)
      return 0;
    return +substring;
  }

  private static unzipSerialRange(serialRange: string): string[] {
    let arrayList: string[] = [];
    let data: string[] = serialRange.split(this.serialRangeSeparator);
    if (data.length > 2)
      console.log("Invalid Serial range " + serialRange);
    if (data.length == 1)
      return [serialRange];
    let startSerial: string = data[0];
    let endSerial: string = data[1];
    if (this.notConsistentWith(startSerial, endSerial) || !this.before(startSerial, endSerial))
      console.log("Invalid Serial range " + serialRange);
    let currentSerial: string = startSerial;
    while (this.before(currentSerial, endSerial)) {
      arrayList.push(currentSerial!);
      currentSerial = this.nextSerial(currentSerial);
    }
    arrayList.push(endSerial);
    return arrayList;
  }

  static before(s1: string | undefined, s2: string | undefined): boolean {
    if (s1 && s2)
      return s1.localeCompare(s2) < 0;
    return false
  }

  private static nextSerial(serialValue: string): string {
    let newSerial: string = "";
    let add: boolean = true;
    for (let i = serialValue.length - 1; i >= 0; i--) {
      let charAt: string = serialValue.charAt(i);
      if (add) {

        if (this.isDigit(charAt)) {
          let digit: number = +charAt;
          if (digit == 9)
            newSerial += "0";
          else {
            digit++;
            add = false;
            newSerial = digit + newSerial;
          }
        } else {
          return '';
        }
      } else {
        newSerial = charAt + newSerial;
      }
    }
    return newSerial;
  }

  private static isDigit(charAt: string) {
    return !isNaN(Number(charAt));
  }

  private static notConsistentWith(serial: string, otherSerial: string): boolean {
    return !this.consistentWith(serial, otherSerial);
  }

  private static consistentWith(serial: string, otherSerial: string): boolean {
    if (ObjectChecker.areNotEqual(this.calcPrefix(serial), this.calcPrefix(otherSerial)))
      return false;
    return this.calcDecimalPartSize(serial) === this.calcDecimalPartSize(otherSerial);
  }

  private static calcPrefix(serial: string): string {
    if (!serial)
      return "";
    return serial.substring(0, serial.length - this.calcDecimalPartSize(serial));
  }

  private static calcDecimalPartSize(serial: string): number {
    if (!serial)
      return 0;
    let length: number = 0;
    for (let i = serial.length - 1; i >= 0; i--) {
      if (!this.isDigit(serial.charAt(i)))
        return length;
      length++;
    }
    return serial.length;
  }
}
